import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Notes_BehaviourType, type Person, usePermissions } from '@tyro/api';
import { getPartyName } from '@tyro/calendar';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RHFAutocomplete,
  RHFCheckbox,
  RHFDateTimePicker,
  RHFRadioGroup,
  RHFSwitch,
  RHFTextField,
  useFormValidator,
  usePreferredNameLayout,
} from '@tyro/core';
import { type GroupSearchParty, useGroupSearchProps } from '@tyro/groups';
import { useTranslation } from '@tyro/i18n';
import { InfoCircleIcon } from '@tyro/icons';
import { behaviourColorByType } from '@tyro/settings';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNoteTagsBehaviour } from '../../api/behaviour/behaviour-tags';
import {
  type ReturnTypeFromUseStudentBehaviour,
  useStudentBehaviour,
} from '../../api/behaviour/student-behaviour';
import { useUpsertStudentBehaviour } from '../../api/behaviour/upsert-behaviour';
import type { StudentsSelectOption } from '../../api/student/students';
import { useSaveTimelineHighlight } from '../../api/student/timeline';
import { getBehaviourType } from '../../utils/get-behaviour-type';
import { RHFStaffAutocomplete } from '../common/staff-autocomplete';
import { RHFStudentAutocomplete } from '../common/student-autocomplete';
import {
  type CreateHighlightFormState,
  eventTypeByBehaviourType,
} from './create-behaviour-highlight-modal';

export type CreateBehaviourFormState = NonNullable<
  ReturnTypeFromUseStudentBehaviour['behaviours']
>[number] & {
  students: StudentsSelectOption;
  noteId: number | null;
  behaviour: NonNullable<CreateBehaviourFormState['tags']>[number];
  note: string;
  associatedGroups: {
    partyId: number;
    text: string;
    avatarUrl?: string;
  }[];
  occurredOn: Dayjs;
  behaviourTypeState: Notes_BehaviourType;
  applyToPresentStudents: boolean;
};

type AdditonalHighlightState = {
  createHighlightFromBehaviour: boolean;
} & CreateHighlightFormState;

type FormState = CreateBehaviourFormState & AdditonalHighlightState;

export type UpsertBehaviourModalProps = {
  noteId?: number;
  open: boolean;
  onClose: () => void;
  behaviourId?: number;
  defaultState?: Partial<CreateBehaviourFormState>;
  behaviourType?: Notes_BehaviourType | 'All';
  setBehaviourType?: (type: Notes_BehaviourType) => void;
};

const writeHighlightPermission = 'ps:1:student_timeline:write_highlights';

export function UpsertBehaviourModal({
  open,
  onClose,
  behaviourId,
  defaultState,
  behaviourType = Notes_BehaviourType.Positive,
  setBehaviourType,
}: UpsertBehaviourModalProps) {
  const { t } = useTranslation(['common', 'people']);
  const { displayName } = usePreferredNameLayout();
  const { hasPermission } = usePermissions();

  const { resolver, rules } = useFormValidator<FormState>();

  const { data: behaviourData, isLoading: isLoadingBehaviourData } =
    useStudentBehaviour(
      { noteIds: [behaviourId ?? 0] },
      {
        enabled: Boolean(open && behaviourId),
      },
    );
  const initialState: Partial<CreateBehaviourFormState> | undefined =
    behaviourData?.behaviours?.[0] ?? defaultState;

  const { control, handleSubmit, reset, watch, setValue } = useForm<FormState>({
    resolver: resolver({
      students: rules.required(),
      occurredOn: rules.required(),
      behaviour: rules.required(),
      title: rules.required(),
      description: rules.required(),
      assignedTo: rules.required(),
    }),
    defaultValues: {
      occurredOn: dayjs(),
      behaviourTypeState: getBehaviourType(initialState?.category),
      createHighlightFromBehaviour: false,
      applyToPresentStudents: false,
    },
  });

  const [
    behaviourTypeOption,
    students,
    createHighlightFromBehaviour,
    associatedGroups,
  ] = watch([
    'behaviourTypeState',
    'students',
    'createHighlightFromBehaviour',
    'associatedGroups',
  ]);
  const studentIds = students?.map(({ partyId }) => partyId) ?? [];
  const searchGroupProps = useGroupSearchProps();

  const { data: behaviourTags = [], isLoading: isLoadingBehaviourTags } =
    useNoteTagsBehaviour();

  const { mutate, isPending } = useUpsertStudentBehaviour();
  const { mutate: createHighlight, isPending: isCreatingHighlight } =
    useSaveTimelineHighlight();

  const onSubmit = handleSubmit(
    ({
      associatedGroups,
      occurredOn,
      behaviour,
      note,
      behaviourTypeState,
      applyToPresentStudents,
      ...otherValues
    }) => {
      const associatedParties = associatedGroups?.map(
        (group) => group?.partyId,
      );

      mutate(
        [
          {
            id: initialState?.noteId,
            note: note ?? initialState?.details,
            referencedParties: studentIds,
            tags: behaviour?.id ? [behaviour?.id] : [],
            associatedParties,
            incidentDate: occurredOn.format('YYYY-MM-DDTHH:mm:ss'),
            applyToPresentStudents,
          },
        ],
        {
          onSuccess: (newBehaviourResponse) => {
            if (setBehaviourType) {
              setBehaviourType(behaviourTypeState);
            }

            if (otherValues.createHighlightFromBehaviour) {
              const { title, description, assignedTo } = otherValues;
              createHighlight(
                {
                  title,
                  highlight: description,
                  assignedToPartyIds: assignedTo.map(({ partyId }) => partyId),
                  studentPartyId: studentIds[0],
                  events: newBehaviourResponse.notes_upsertNotes.map(
                    ({ id, tags }) => {
                      const behaviourType = tags![0]?.behaviourType;
                      const eventType =
                        eventTypeByBehaviourType[behaviourType!];

                      return {
                        eventId: id!.toString(),
                        eventType,
                      };
                    },
                  ),
                },
                {
                  onSuccess: () => {
                    onClose();
                  },
                },
              );
            } else {
              onClose();
            }
          },
        },
      );
    },
  );

  const filterTagsByBehaviourType = useMemo(
    () =>
      behaviourTags?.filter((tag) => tag.behaviourType === behaviourTypeOption),
    [behaviourTypeOption, behaviourTags],
  );

  useEffect(() => {
    const isInitialBehaviourType =
      initialState?.tags?.[0]?.behaviourType === behaviourTypeOption;

    if (isInitialBehaviourType) {
      setValue('behaviour', initialState?.tags?.[0] ?? null);
    } else {
      setValue('behaviour', null);
    }
  }, [behaviourTypeOption, initialState, setValue]);

  useEffect(() => {
    let initStudents = initialState?.students;

    if (!initStudents) {
      initStudents = initialState?.referencedParties ?? [];
    }

    reset({
      ...(initialState ?? {}),
      students: initStudents as Person[],
      associatedGroups:
        initialState?.associatedGroups ??
        initialState?.associatedParties?.map((party) => ({
          partyId: party?.partyId,
          text: getPartyName(party, displayName),
        })),
      behaviour: initialState?.tags?.[0] ?? undefined,
      note: initialState?.details,
      behaviourTypeState: initialState?.tags?.[0]?.behaviourType ?? undefined,
      occurredOn: dayjs(initialState?.incidentDate || undefined),
      createHighlightFromBehaviour: false,
      applyToPresentStudents: false,
    });
  }, [initialState, behaviourType]);

  const isCreateHighlightDisabled = studentIds.length > 1;

  useEffect(() => {
    if (isCreateHighlightDisabled) {
      setValue('createHighlightFromBehaviour', false);
    }
  }, [isCreateHighlightDisabled]);

  const showFilterAbsentStudents = associatedGroups?.length === 1;

  useEffect(() => {
    if (!showFilterAbsentStudents) {
      setValue('applyToPresentStudents', false);
    }
  }, [showFilterAbsentStudents, setValue]);

  const isLoading = isLoadingBehaviourTags || isLoadingBehaviourData;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      fullWidth
      maxWidth="sm"
    >
      <form onSubmit={onSubmit}>
        {isLoading ? (
          <Stack minHeight="40vh" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Stack>
        ) : (
          <>
            <DialogTitle onClose={onClose}>
              {initialState?.noteId
                ? t('people:editBehaviour')
                : t('people:createBehaviour')}
            </DialogTitle>
            <DialogContent>
              <Stack gap={3} mt={1}>
                <RHFStudentAutocomplete
                  multiple
                  label={t('common:students')}
                  controlProps={{
                    name: 'students',
                    control,
                  }}
                  limitTags={3}
                />
                <RHFDateTimePicker
                  label={t('common:date')}
                  controlProps={{
                    name: 'occurredOn',
                    control,
                  }}
                />
                <RHFAutocomplete<FormState, GroupSearchParty, true>
                  {...searchGroupProps}
                  label={t('people:associations')}
                  controlProps={{ name: 'associatedGroups', control }}
                />
                <Collapse in={showFilterAbsentStudents} unmountOnExit>
                  <Stack direction="row" alignItems="center">
                    <RHFSwitch
                      label={t('people:excludeAbsentStudents')}
                      controlProps={{
                        name: 'applyToPresentStudents',
                        control,
                      }}
                    />
                    <Tooltip
                      title={t('people:excludeAbsentStudentsTooltip')}
                      placement="top"
                    >
                      <InfoCircleIcon sx={{ width: 18, height: 18 }} />
                    </Tooltip>
                  </Stack>
                </Collapse>

                <RHFRadioGroup
                  radioGroupProps={{ sx: { flexDirection: 'row' } }}
                  label={t('people:behaviourType')}
                  options={[
                    Notes_BehaviourType.Positive,
                    Notes_BehaviourType.Negative,
                    Notes_BehaviourType.Neutral,
                  ].map((option) => ({
                    value: option,
                    label: t(`people:behaviourTypes.${option}`),
                  }))}
                  controlProps={{
                    name: 'behaviourTypeState',
                    defaultValue: behaviourType,
                    control,
                  }}
                />
                <RHFAutocomplete
                  fullWidth
                  filterOptions={(options) =>
                    options.filter((option) => option.availableToStaff)
                  }
                  options={filterTagsByBehaviourType || []}
                  label={t('people:tags')}
                  optionIdKey="id"
                  optionTextKey="name"
                  renderTag={(tag, renderTag) => {
                    return renderTag(
                      <Chip
                        size="small"
                        variant="soft"
                        color={
                          behaviourColorByType.get(tag?.behaviourType!) ??
                          'slate'
                        }
                        label={tag.name}
                      />,
                    );
                  }}
                  controlProps={{
                    name: 'behaviour',
                    control,
                  }}
                />
                <RHFTextField
                  label={t('common:details')}
                  controlProps={{
                    name: 'note',
                    control,
                  }}
                  textFieldProps={{
                    fullWidth: true,
                    multiline: true,
                    rows: 4,
                  }}
                />

                {hasPermission(writeHighlightPermission) && (
                  <>
                    <Tooltip
                      title={
                        isCreateHighlightDisabled
                          ? t(
                              'people:canOnlyCreateHighlightsForAnIndividualStudent',
                            )
                          : undefined
                      }
                    >
                      <span>
                        <RHFCheckbox
                          label={t('people:createHighlightFromBehaviour')}
                          controlProps={{
                            name: 'createHighlightFromBehaviour',
                            control,
                          }}
                          checkboxProps={{
                            disabled: isCreateHighlightDisabled,
                          }}
                        />
                      </span>
                    </Tooltip>
                    <Collapse in={createHighlightFromBehaviour} unmountOnExit>
                      <Stack gap={3}>
                        <Typography component="h3" variant="subtitle1">
                          {t('people:highlightDetails')}
                        </Typography>
                        <RHFTextField
                          label={t('common:title')}
                          controlProps={{
                            name: 'title',
                            control,
                          }}
                        />
                        <RHFTextField
                          label={t('common:description')}
                          controlProps={{
                            name: 'description',
                            control,
                          }}
                          textFieldProps={{
                            fullWidth: true,
                            multiline: true,
                            rows: 4,
                          }}
                        />
                        <RHFStaffAutocomplete
                          label={t('people:assignedTo')}
                          multiple
                          controlProps={{
                            name: 'assignedTo',
                            control,
                          }}
                        />
                      </Stack>
                    </Collapse>
                  </>
                )}
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button variant="soft" color="inherit" onClick={onClose}>
                {t('common:actions.cancel')}
              </Button>

              <Button
                type="submit"
                variant="contained"
                loading={isPending || isCreatingHighlight}
              >
                {t('common:actions.save')}
              </Button>
            </DialogActions>
          </>
        )}
      </form>
    </Dialog>
  );
}
